import { faCreditCard, faReceipt } from "@fortawesome/free-solid-svg-icons";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { usePermissionModal } from "contexts/PermissionModalContext";
import { formatCurrency, formatPercentage } from "utils/formatters";
import { DashCard, DashCardHeader, DashCardBody } from "./v2/DashCard";
import DashEmptyState from "./v2/DashEmptyState";

interface FormaPagamentoPercentual {
  tipo: string;
  valor: number;
  percentual: number;
}

interface ObterPercentualFormaPagamentoResponse {
  percentualPbm: number;
  percentualCartao: number;
  formas: FormaPagamentoPercentual[];
}

const NOME_TIPO_PAGAMENTO: Record<string, string> = {
  Dinheiro: "Dinheiro",
  CartaoCredito: "Cartão de Crédito",
  CartaoDebito: "Cartão de Débito",
  Cheque: "Cheque",
  Boleto: "Boleto",
  TransferenciaBancaria: "Transferência Bancária",
  Crediario: "Crediário",
  PBM: "PBM",
  Convenio: "Convênio",
  Outros: "Outros",
};

const formatTipoPagamento = (tipo: string) => NOME_TIPO_PAGAMENTO[tipo] ?? tipo;

const PercentualFormaPagamento = () => {
  const { getQueryRetryHandler } = usePermissionModal();

  const { data, isLoading, error } = useQuery({
    queryKey: ["percentualFormaPagamento"],
    queryFn: async () =>
      (
        await axios.get<ObterPercentualFormaPagamentoResponse>(
          "/api/dashboard/vendas/percentual-forma-pagamento",
        )
      ).data,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: getQueryRetryHandler({
      queryKey: ["percentualFormaPagamento"],
      defaultMessage: "Você não possui permissão para visualizar as formas de pagamento.",
      showModal: false,
    }),
  });

  const isAccessDenied = axios.isAxiosError(error) && error?.response?.status === 403;
  if (isAccessDenied) return null;

  const formas = (data?.formas ?? []).filter((forma) => forma.valor > 0);

  const highlights = [
    { key: "pbm", label: "PBM", value: data?.percentualPbm ?? 0, accent: "accent" as const },
    { key: "cartao", label: "Cartão", value: data?.percentualCartao ?? 0, accent: "accent2" as const },
  ].filter((h) => h.value > 0);

  return (
    <DashCard hoverable={false}>
      <DashCardHeader
        icon={faCreditCard}
        variant="accent"
        title="Formas de Pagamento"
        tooltip="Percentual sobre o total vendido no período. Modalidades sem venda no período não são exibidas."
      />
      <DashCardBody>
        {isLoading ? (
          <>
            <div className="d-flex gap-2 mb-3">
              <div className="dash-skeleton" style={{ height: 56, flex: 1 }} />
              <div className="dash-skeleton" style={{ height: 56, flex: 1 }} />
            </div>
            {Array(4)
              .fill(0)
              .map((_, i) => (
                <div key={i} className="dash-skeleton mb-2" style={{ height: 14 }} />
              ))}
          </>
        ) : formas.length === 0 ? (
          <DashEmptyState icon={faReceipt} title="Nenhuma venda encontrada" description="Não há dados de forma de pagamento para o período." />
        ) : (
          <>
            {highlights.length > 0 && (
              <div className={`dash-grid mb-3 ${highlights.length > 1 ? "dash-grid-2" : ""}`}>
                {highlights.map((h) => (
                  <div
                    key={h.key}
                    style={{
                      background: h.accent === "accent" ? "var(--dash-accent-tint)" : "var(--dash-accent-2-tint)",
                      borderRadius: 10,
                      padding: "0.9rem",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "var(--dash-font-display)",
                        fontWeight: 600,
                        fontSize: "1.4rem",
                        color: h.accent === "accent" ? "var(--dash-accent)" : "var(--dash-accent-2)",
                      }}
                    >
                      {formatPercentage(h.value)}
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "var(--dash-muted)" }}>{h.label}</div>
                  </div>
                ))}
              </div>
            )}
            <div>
              {formas.map((forma, index) => (
                <div
                  key={index}
                  className="d-flex justify-content-between align-items-center"
                  style={{
                    padding: "0.55rem 0",
                    borderBottom: index < formas.length - 1 ? "1px solid var(--dash-border-soft)" : "none",
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: "0.875rem" }}>{formatTipoPagamento(forma.tipo)}</span>
                  <span className="d-flex align-items-center gap-3" style={{ fontFamily: "var(--dash-font-mono)", fontSize: "0.8125rem" }}>
                    <span style={{ color: "var(--dash-muted)" }}>{formatCurrency(forma.valor)}</span>
                    <span style={{ color: "var(--dash-ink)", fontWeight: 600 }}>{formatPercentage(forma.percentual)}</span>
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </DashCardBody>
    </DashCard>
  );
};

export default PercentualFormaPagamento;
